import { useState, useEffect, useRef } from 'react';

interface UseTypingAnimationOptions {
  content: string;
  isStreaming?: boolean;
  speed?: number; // ms per batch (default 20)
  charsPerBatch?: number; // characters to reveal per batch (default 3)
}

interface UseTypingAnimationResult {
  displayedContent: string;
  newContent: string; // The newly revealed content for fade-in effect
  isAnimating: boolean;
}

/**
 * Hook to create a typewriter animation effect for streaming text.
 *
 * - For messages that start streaming, animates in small batches with fade
 * - Continues animation even after streaming ends (queues content)
 * - Historical messages (never streamed) show immediately
 * - Returns both displayed content and newly added content for fade effect
 *
 * @param content - The full text content to display
 * @param isStreaming - Whether the message is currently being streamed
 * @param speed - Milliseconds per batch (default 20ms)
 * @param charsPerBatch - Characters to reveal per batch (default 3)
 * @returns Object with displayedContent, newContent, and isAnimating
 */
export function useTypingAnimation({
  content,
  isStreaming = false,
  speed = 20,
  charsPerBatch = 3,
}: UseTypingAnimationOptions): UseTypingAnimationResult {
  const [displayedLength, setDisplayedLength] = useState(0);
  const [prevDisplayedLength, setPrevDisplayedLength] = useState(0);
  // Initialize the should-animate flag synchronously from the initial
  // `isStreaming` prop so a message inserted with `isStreaming: true` doesn't
  // render its full content on the first frame before the useEffect below
  // gets a chance to flip the flag. Previously this caused a brief flash of
  // the full message followed by an empty bubble that then typed in.
  const shouldAnimateRef = useRef(isStreaming);

  // Latch the flag if isStreaming becomes true later (e.g., a message that
  // started rendered then began streaming new content).
  useEffect(() => {
    if (isStreaming && !shouldAnimateRef.current) {
      shouldAnimateRef.current = true;
    }
  }, [isStreaming]);

  useEffect(() => {
    // Don't animate if this message never started streaming (historical message)
    if (!shouldAnimateRef.current) {
      setDisplayedLength(content.length);
      setPrevDisplayedLength(content.length);
      return;
    }

    // Already caught up to content
    if (displayedLength >= content.length) {
      return;
    }

    // Animate next batch of characters
    const timeout = setTimeout(() => {
      setPrevDisplayedLength(displayedLength);
      setDisplayedLength((prev) => Math.min(prev + charsPerBatch, content.length));
    }, speed);

    return () => clearTimeout(timeout);
  }, [content, content.length, displayedLength, speed, charsPerBatch]);

  // For non-animated messages, return full content
  if (!shouldAnimateRef.current) {
    return {
      displayedContent: content,
      newContent: '',
      isAnimating: false,
    };
  }

  const displayedContent = content.slice(0, prevDisplayedLength);
  const newContent = content.slice(prevDisplayedLength, displayedLength);
  const isAnimating = displayedLength < content.length;

  return {
    displayedContent,
    newContent,
    isAnimating,
  };
}

export default useTypingAnimation;
